import {
  AccountingDateCalculationStrategyEnum,
  AutoPaymentCollectionTimingEnum,
  BillGroupStatusEnum,
  FirstInvoiceCreationStrategyEnum,
  IBillGroupResp,
  IBillingSchedulePeriodItemsSchema,
  IBillingSchedulePeriodRespSchema,
  INetTerm,
  InvoiceCreationEnum,
  NetTermsEnum,
  QuoteSettingsDefaultAddressSourceEnum,
} from '@monetize/types/app';
import { MimeTypeEnum } from '@monetize/types/common';
import { getFormattedDate } from '@monetize/utils/core';
import isString from 'lodash/isString';
import { unparse } from 'papaparse';
import { apiGet } from '../../../api/axios';
import { EXPORT } from '../../../constants';
import { saveFile } from '../../../utils/download';

/**
 * Converts BillGroupResp to a form-compatible object with only BillGroupFormSchema fields
 */
export const convertBillGroupRespToFormData = (
  bgResp?: IBillGroupResp,
  defaultNetTerm?: INetTerm | null,
) => {
  // Default values
  if (!bgResp) {
    return {
      customId: '',
      name: '',
      status: BillGroupStatusEnum.ACTIVE,
      billDay: new Date().getDate(),
      netTerms: defaultNetTerm?.value || NetTermsEnum.NET_0,
      contactId: '',
      shippingContactId: '',
      defaultPaymentGatewayId: null,
      paymentMethodId: null,
      dunningProcessId: null,
      collectInvoiceBalanceAutomatically: true,
      autoPaymentCollectionTiming:
        AutoPaymentCollectionTimingEnum.INVOICE_GENERATION,
      autoEmailInvoice: true,
      invoiceCreation: InvoiceCreationEnum.AUTOMATIC,
      firstInvoiceCreationStrategy:
        FirstInvoiceCreationStrategyEnum.FOLLOW_EXISTING_SETTINGS,
      invoiceDelay: 0,
      purchaseOrderNumber: null,
      registrationNumber: null,
      vatNumber: '',
      legalEntityId: '',
      currency: 'USD',
      customFields: {},
      addressData: {
        addressSource: QuoteSettingsDefaultAddressSourceEnum.CONTACT,
      },
      accountingDateCalculationStrategy:
        AccountingDateCalculationStrategyEnum.BILLING_ANNIVERSARY,
      billingAccountId: null,
      linkedAccountId: null,
      strCcEmails: null,
      strBccEmails: null,
      outputLanguage: null,
    };
  }

  return {
    customId: bgResp.customId,
    name: bgResp.name,
    status: bgResp.status,
    billDay: bgResp.billDay,
    netTerms: bgResp.netTerms,
    contactId: bgResp.billingContactId,
    shippingContactId: bgResp.shippingContactId,
    defaultPaymentGatewayId: bgResp.defaultPaymentGateway?.id,
    paymentMethodId: bgResp.paymentMethodId,
    dunningProcessId: bgResp.dunningProcessId,
    collectInvoiceBalanceAutomatically:
      bgResp.collectInvoiceBalanceAutomatically,
    autoPaymentCollectionTiming: bgResp.autoPaymentCollectionTiming,
    autoEmailInvoice: bgResp.autoEmailInvoice,
    invoiceCreation: bgResp.invoiceCreation,
    firstInvoiceCreationStrategy: bgResp.firstInvoiceCreationStrategy,
    invoiceDelay: bgResp.invoiceDelay,
    strCcEmails: bgResp.ccEmails ? bgResp.ccEmails?.join(',') : null,
    strBccEmails: bgResp.bccEmails ? bgResp.bccEmails?.join(',') : null,
    purchaseOrderNumber: bgResp.purchaseOrderNumber,
    registrationNumber: bgResp.registrationNumber,
    vatNumber: bgResp.vatNumber,
    legalEntityId: bgResp.legalEntityId,
    currency: bgResp.currency,
    customFields: bgResp.customFields,
    addressData: {
      addressSource: bgResp.addressSource,
      billingAddressId: bgResp.billingAddress?.id,
      shippingAddressId: bgResp.shippingAddress?.id,
    },
    accountingDateCalculationStrategy: bgResp.accountingDateCalculationStrategy,
    billingAccountId: bgResp.billingAccountId || bgResp.accountId,
    linkedAccountId: bgResp.linkedAccountId,
    outputLanguage: bgResp.outputLanguage || null,
  };
};

type InvoiceFlattenedItem = Record<string, string | number>;

export const flattenInvoices = ({
  invoices,
  billGroupId,
  accountId,
}: {
  invoices: Record<string, IBillingSchedulePeriodRespSchema[]>;
  billGroupId: string;
  accountId: string;
}) => {
  const flattenedData: InvoiceFlattenedItem[] = [];

  Object.entries(invoices).forEach(([contractId, invoiceList]) => {
    invoiceList.forEach((invoice) => {
      invoice.items.forEach((item: IBillingSchedulePeriodItemsSchema) => {
        const flattenedItem: InvoiceFlattenedItem = {
          contractId,
          billGroupId,
          accountId,
          invoiceDate: invoice.invoiceDate,
          invoiceId: invoice.invoiceId ?? '',
          status: invoice.status,
          invoiceStatus: invoice.invoiceStatus ?? '',
          servicePeriodStart: item.servicePeriod.start,
          servicePeriodEnd: item.servicePeriod.end,
          offeringName: item.offeringName,
          productName: item.productName,
          quantity: item.quantity,
          pricePerUnit: item.pricePerUnit,
          amount: item.amount,
        };
        flattenedData.push(flattenedItem);
      });
    });
  });
  return flattenedData;
};

export const downloadInvoices = async ({ contractIds, billGroup }: any) => {
  const { endpoint: _endpoint, fields } =
    EXPORT.EXPORT_CONFIGURATION.billingSchedule;
  const currentDate = getFormattedDate(new Date(), 'yyyy-MM-dd');
  const headers = [...fields];

  const allResults: Record<string, any> = {};
  if (contractIds.length > 1) {
    for (const contractId of contractIds) {
      const endpoint = isString(_endpoint) ? _endpoint : _endpoint(contractId);

      const results = await apiGet<any>(endpoint);
      allResults[contractId] = results.data.periods;
    }
  } else {
    const singleContractId = contractIds[0];
    const endpoint = isString(_endpoint)
      ? _endpoint
      : _endpoint(singleContractId);

    const results = await apiGet<any>(endpoint);
    allResults[singleContractId] = results.data.periods;
  }

  const csv = unparse(
    {
      fields: headers,
      data: flattenInvoices({
        invoices: allResults,
        billGroupId: billGroup.id,
        accountId: billGroup.accountId,
      }),
    },
    { header: true },
  );
  saveFile(
    csv,
    `${currentDate}-bill-schedule-${billGroup.accountId}.csv`,
    MimeTypeEnum.CSV,
  );
};
